import { WHATSAPP_NUMBER } from '@/data/constants';
import { WhatsAppIcon } from '@/components/icons';

interface Plan {
  id: string;
  name: string;
  price: string;
  period: string;
  highlighted: boolean;
  badge?: string;
}

const plans: Plan[] = [
  {
    id: '1',
    name: 'Day Pass',
    price: '$10',
    period: 'per day',
    highlighted: false,
  },
  {
    id: '2',
    name: 'Full Month',
    price: '$70',
    period: 'per month',
    highlighted: true,
    badge: 'Most Popular',
  },
  {
    id: '3',
    name: 'Half Month',
    price: '$45',
    period: 'per 15 days',
    highlighted: false,
  },
];

function buildWhatsAppLink(plan: Plan) {
  const msg = `Hi HUB30! I'm interested in the ${plan.name} membership (${plan.price} ${plan.period}).`;
  return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(msg)}`;
}

export default function Membership() {
  return (
    <section id="membership" className="bg-black py-16 lg:py-32 px-4 lg:px-8 xl:px-16">
      {/* Header */}
      <div className="text-center mb-16">
        <span data-animate className="inline-flex items-center justify-center gap-2 text-[#14B8A6] text-xs tracking-widest font-medium mb-4">
          <span className="w-4 h-px bg-[#14B8A6]" />
          PRICING
          <span className="w-4 h-px bg-[#14B8A6]" />
        </span>
        <h2 data-animate data-delay="1" className="text-white text-4xl lg:text-6xl font-bold mb-4">Choose Your Plan</h2>
        <p data-animate data-delay="2" className="text-gray-400 text-base mt-2 max-w-lg mx-auto">
          Visit us in person or contact us via WhatsApp to get started.
        </p>
      </div>

      {/* Cards */}
      <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 items-stretch">
        {plans.map((plan, index) => (
          <div
            key={plan.id}
            data-animate
            data-delay={(index + 1) as 1 | 2 | 3}
            className={`relative rounded-3xl flex flex-col transition-all duration-300 ${
              plan.highlighted
                ? 'bg-[#14B8A6] shadow-2xl shadow-teal-500/25 scale-[1.03]'
                : 'bg-[#141414] border border-white/[0.08] hover:border-[#14B8A6]/30 hover:-translate-y-1 hover:shadow-[0_20px_50px_-12px_rgba(0,0,0,0.5)]'
            }`}
          >
            {/* Most Popular badge */}
            {plan.badge && (
              <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                <span className="bg-black text-[#14B8A6] text-xs font-bold px-4 py-1.5 rounded-full border border-[#14B8A6]/30 whitespace-nowrap shadow-lg">
                  ★ {plan.badge}
                </span>
              </div>
            )}

            <div className="p-6 lg:p-8 flex flex-col flex-1 items-center text-center">
              {/* Plan name */}
              <p className={`text-xs font-semibold uppercase tracking-widest mb-6 ${plan.highlighted ? 'text-black/60' : 'text-gray-500'}`}>
                {plan.name}
              </p>

              {/* Price */}
              <div className="mb-8">
                <div className={`text-5xl lg:text-6xl font-bold leading-none mb-1 ${plan.highlighted ? 'text-black' : 'text-white'}`}>
                  {plan.price}
                </div>
                <div className={`text-sm mt-1 ${plan.highlighted ? 'text-black/60' : 'text-gray-500'}`}>
                  {plan.period}
                </div>
              </div>

              {/* CTA */}
              <a
                href={buildWhatsAppLink(plan)}
                target="_blank"
                rel="noopener noreferrer"
                className={`flex items-center justify-center gap-2.5 w-full py-3.5 rounded-2xl font-semibold text-sm transition-all duration-200 ${
                  plan.highlighted
                    ? 'bg-black text-white hover:bg-black/80 hover:shadow-[0_0_20px_rgba(0,0,0,0.4)]'
                    : 'bg-white/[0.06] text-white hover:bg-[#14B8A6] hover:shadow-[0_0_20px_rgba(20,184,166,0.3)] border border-white/[0.08]'
                }`}
              >
                <WhatsAppIcon className="text-base" />
                Get Started
              </a>
            </div>
          </div>
        ))}
      </div>

      {/* Footer note */}
      <div data-animate className="mt-10 max-w-md mx-auto">
        <div className="flex items-start gap-3 bg-white/[0.04] border border-white/[0.1] rounded-2xl px-5 py-4">
          <i className="ri-information-line text-[#14B8A6] text-lg flex-shrink-0 mt-0.5" />
          <p className="text-gray-300 text-sm leading-relaxed">
            All plans include full gym floor access.{' '}
            <span className="text-white font-semibold">Class access is an additional $20.</span>
          </p>
        </div>
      </div>
    </section>
  );
}
